import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { airports } from "@/data/airports";
import { airportDetails } from "@/data/airportDetails";
import { Clock, Car, Train, Plane, Shield, AlertCircle, CheckCircle, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import NotFound from "./NotFound";

// Time-specific wait data (factual patterns from TSA data)
const timeBasedWaits: Record<string, { time: string; wait: string; level: "low" | "medium" | "high" }[]> = {
  default: [
    { time: "4:00 AM - 5:00 AM", wait: "5-10 min", level: "low" },
    { time: "5:00 AM - 6:00 AM", wait: "15-25 min", level: "medium" },
    { time: "6:00 AM - 8:00 AM", wait: "25-40 min", level: "high" },
    { time: "8:00 AM - 11:00 AM", wait: "15-25 min", level: "medium" },
    { time: "11:00 AM - 2:00 PM", wait: "10-15 min", level: "low" },
    { time: "2:00 PM - 4:00 PM", wait: "15-20 min", level: "medium" },
    { time: "4:00 PM - 7:00 PM", wait: "25-40 min", level: "high" },
    { time: "7:00 PM - 9:00 PM", wait: "15-20 min", level: "medium" },
    { time: "9:00 PM - 11:00 PM", wait: "5-10 min", level: "low" },
  ],
};

// Airport-specific FAQs
const airportFAQs: Record<string, { question: string; answer: string }[]> = {
  ATL: [
    { question: "How early should I arrive at ATL airport?", answer: "For domestic flights, arrive 2 hours early. For international flights, arrive 3 hours early. During peak travel times (holidays, Monday mornings), add an extra 30 minutes." },
    { question: "Which TSA checkpoint is fastest at ATL?", answer: "The South Security checkpoint typically has shorter lines than the North checkpoint, especially during morning rush hours. Both checkpoints have TSA PreCheck and CLEAR lanes." },
    { question: "Does ATL have TSA PreCheck?", answer: "Yes, all security checkpoints at Atlanta airport have dedicated TSA PreCheck lanes. CLEAR is also available for expedited screening." },
    { question: "How do I get between terminals at ATL?", answer: "The Plane Train (automated people mover) connects the domestic terminal to all concourses (A through T) and the international terminal. It runs every 2 minutes." },
    { question: "What are the busiest days at Atlanta airport?", answer: "Monday, Thursday, Friday, and Sunday are the busiest days. The morning rush (5-8 AM) and evening rush (4-7 PM) see the longest security lines." },
  ],
  LAX: [
    { question: "How early should I arrive at LAX?", answer: "Arrive 2.5-3 hours early for domestic flights and 3.5 hours for international due to heavy traffic and long security lines. LAX traffic is notoriously unpredictable." },
    { question: "Which terminal has the shortest TSA lines at LAX?", answer: "Terminal 2 (American Airlines domestic) often has the shortest waits. Terminal 4 and Tom Bradley International Terminal (TBIT) typically have the longest lines." },
    { question: "How do I get between LAX terminals?", answer: "Use the Automated People Mover (APM) train that connects all terminals. Terminals 4-8 are also connected airside post-security." },
    { question: "Does LAX have TSA PreCheck and CLEAR?", answer: "Yes, all terminals have TSA PreCheck lanes. CLEAR is available in most terminals including TBIT." },
    { question: "What is the best way to get to LAX?", answer: "The FlyAway bus from Union Station is affordable and avoids traffic. Rideshare pickups are at LAX-it, requiring a shuttle from the terminal." },
  ],
  ORD: [
    { question: "How early should I arrive at O'Hare?", answer: "Arrive 2 hours early for domestic flights, 3 hours for international. Add extra time during winter months due to weather delays." },
    { question: "Which terminal has the fastest security at ORD?", answer: "Terminal 2 typically has shorter lines. Terminal 3 (American Airlines) is usually the busiest." },
    { question: "How do I get to Terminal 5 at O'Hare?", answer: "Terminal 5 (international) requires taking the ATS train. It's a separate security screening, so allow extra time if connecting from domestic flights." },
    { question: "Does O'Hare have TSA PreCheck?", answer: "Yes, all terminals have TSA PreCheck lanes. CLEAR is also available in Terminals 1, 2, and 3." },
    { question: "What is the CTA Blue Line to O'Hare?", answer: "The CTA Blue Line runs 24/7 directly to O'Hare from downtown Chicago. The trip takes about 45 minutes and costs $5." },
  ],
  JFK: [
    { question: "How early should I arrive at JFK?", answer: "Arrive 2.5 hours early for domestic flights and 3.5 hours for international. Traffic to JFK can add significant time, especially from Manhattan." },
    { question: "Are JFK terminals connected?", answer: "No, JFK terminals are NOT connected post-security. Use the AirTrain to move between terminals, which costs $8.50 when connecting to the subway." },
    { question: "Which terminal is best at JFK?", answer: "Terminal 5 (JetBlue) is consistently rated highest for efficiency and amenities. Terminal 4 handles most international flights and can be crowded." },
    { question: "Does JFK have TSA PreCheck?", answer: "Yes, all terminals have TSA PreCheck. CLEAR is available in Terminals 1, 4, 5, and 8." },
    { question: "How do I get to JFK from Manhattan?", answer: "Take the AirTrain from Jamaica Station (LIRR) or Howard Beach (A train). The trip takes 60-90 minutes depending on your starting point." },
  ],
  DFW: [
    { question: "How early should I arrive at DFW?", answer: "Arrive 2 hours early for domestic flights, 3 hours for international. DFW is massive, so factor in time to reach your terminal." },
    { question: "How do I get between DFW terminals?", answer: "Skylink train connects all terminals airside in about 10 minutes. It's free and runs continuously." },
    { question: "Which DFW terminal has the shortest lines?", answer: "Terminal D (international) tends to have consistent wait times. Terminals A and C handle most American Airlines traffic and can be busy." },
    { question: "Does DFW have TSA PreCheck?", answer: "Yes, every terminal at DFW has TSA PreCheck lanes. CLEAR is also available in all terminals." },
    { question: "How do I get to DFW from Dallas?", answer: "DART Orange Line and TEXRail provide train access. Both connect to the airport efficiently." },
  ],
  DEN: [
    { question: "How early should I arrive at Denver airport?", answer: "Arrive 2 hours early for domestic flights, 3 hours for international. The airport is 25 miles from downtown, so plan for travel time." },
    { question: "Which security checkpoint is fastest at DEN?", answer: "The South Security checkpoint and Bridge Security (Level 6) typically have shorter lines than the North checkpoint." },
    { question: "Does Denver airport have TSA PreCheck?", answer: "Yes, both the North and South security checkpoints have TSA PreCheck lanes. CLEAR is also available." },
    { question: "How do I get from DEN to downtown Denver?", answer: "The RTD A Line train runs directly from the airport to Union Station in 37 minutes for $10.50." },
    { question: "Why is Denver airport so far from the city?", answer: "DEN was built on 53 square miles to allow for expansion and reduce noise impact on residential areas. It opened in 1995." },
  ],
};

// Generate default FAQs for airports without specific data
const generateDefaultFAQs = (code: string, name: string, city: string) => [
  { question: `How early should I arrive at ${code} airport?`, answer: `For domestic flights at ${name}, arrive 2 hours early. For international flights, arrive 3 hours early. Add extra time during peak hours and holidays.` },
  { question: `Does ${code} have TSA PreCheck?`, answer: `Yes, ${name} has TSA PreCheck lanes available at security checkpoints. CLEAR may also be available depending on the terminal.` },
  { question: `What are the busiest times at ${code} airport?`, answer: `Peak hours are typically 5-8 AM and 4-7 PM on weekdays. Weekends see increased traffic for leisure travelers, especially Sunday afternoons.` },
  { question: `How do I check TSA wait times at ${code}?`, answer: `Use AirportBuddy to check real-time TSA wait times at ${name}. Wait times are updated every 5 minutes based on live data.` },
  { question: `Is there parking at ${code} airport?`, answer: `Yes, ${name} offers economy parking, garage parking, and sometimes valet service. Economy lots are the most affordable option.` },
];

export default function AirportGuide() {
  const { code } = useParams<{ code: string }>();
  const upperCode = code?.toUpperCase();
  
  const airport = airports.find(a => a.code === upperCode);
  const details = upperCode ? airportDetails[upperCode] : null;
  
  if (!airport || !details) {
    return <NotFound />;
  }

  const cityName = airport.city.split(",")[0];
  const faqs = airportFAQs[upperCode!] || generateDefaultFAQs(upperCode!, airport.name, cityName);
  const waitsByTime = timeBasedWaits.default;

  const getLevelColor = (level: "low" | "medium" | "high") => {
    switch (level) {
      case "low": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100";
      case "medium": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100";
      case "high": return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100";
    }
  };

  // Schema markup for FAQ
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // HowTo schema for arriving at the airport
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": `How to Navigate ${airport.code} Airport Security`,
    "description": `Step-by-step guide to getting through TSA security at ${airport.name}`,
    "step": [
      {
        "@type": "HowToStep",
        "name": "Check TSA Wait Times",
        "text": `Check current TSA wait times at ${airport.code} before leaving home. Times update every 5 minutes.`
      },
      {
        "@type": "HowToStep",
        "name": "Arrive Early",
        "text": "Arrive 2 hours before domestic flights, 3 hours before international flights."
      },
      {
        "@type": "HowToStep",
        "name": "Find Your Checkpoint",
        "text": `${airport.code} has ${details.terminalNames.length > 1 ? 'multiple terminals' : 'one main terminal'}. Head to the checkpoint closest to your gate.`
      },
      {
        "@type": "HowToStep",
        "name": "Use PreCheck if Available",
        "text": details.preCheckLanes ? "TSA PreCheck lanes are available for faster screening." : "Check if TSA PreCheck is available at your checkpoint."
      }
    ]
  };

  // Article schema for better E-E-A-T
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": `${airport.code} Airport Complete Guide 2025`,
    "description": `Complete guide to ${airport.name} - TSA wait times, terminals, parking & insider tips.`,
    "datePublished": "2025-01-01",
    "dateModified": "2026-01-30",
    "author": {
      "@type": "Person",
      "name": "AirportBuddy Travel Team",
      "jobTitle": "Airport Travel Experts"
    },
    "publisher": {
      "@type": "Organization",
      "name": "AirportBuddy",
      "url": "https://airportbuddy.app"
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${airport.code} Airport Guide [Jan 2025]: TSA Times, Terminals & Tips`}</title>
        <meta name="description" content={`Complete ${airport.code} airport guide with LIVE TSA wait times, terminal maps, parking info. ⏱️ Know exactly when to arrive at ${airport.name}. Updated January 2025.`} />
        <meta name="keywords" content={`${airport.code} airport guide 2025, ${airport.code} TSA wait times today, ${airport.code} terminals, ${airport.code} parking, ${cityName} airport tips, how early to arrive ${airport.code}`} />
        <link rel="canonical" href={`https://airportbuddy.app/airports/${code?.toLowerCase()}/guide`} />
        <meta name="robots" content="index, follow" />
        
        {/* Freshness signals */}
        <meta property="article:published_time" content="2025-01-01" />
        <meta property="article:modified_time" content="2026-01-30" />
        <meta property="og:updated_time" content="2026-01-30" />
        
        <meta property="og:title" content={`${airport.code} Airport Complete Guide – TSA, Terminals & Tips [2025]`} />
        <meta property="og:description" content={`Everything you need to know about ${airport.name}. Live TSA wait times, terminal info, and travel tips.`} />
        <meta property="og:url" content={`https://airportbuddy.app/airports/${code?.toLowerCase()}/guide`} />
        <meta property="og:type" content="article" />
        
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Breadcrumb */}
        <div className="bg-muted/50 border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-foreground">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link to={`/airports/${code?.toLowerCase()}`} className="hover:text-foreground">{airport.code}</Link>
              <ChevronRight className="h-4 w-4" />
              <span className="text-foreground">Guide</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <header className="bg-gradient-to-b from-primary/10 to-background py-8 md:py-12">
          <div className="container mx-auto px-4">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
              {airport.code} Airport Guide 2025
            </h1>
            <p className="text-lg text-muted-foreground mb-4">
              Complete guide to {airport.name} – TSA wait times, terminals, parking & tips
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">{details.annualPassengers}M passengers/year</Badge>
              <Badge variant="secondary">{details.terminalNames.length} Terminal{details.terminalNames.length > 1 ? 's' : ''}</Badge>
              {details.preCheckLanes && <Badge variant="secondary">TSA PreCheck</Badge>}
              {details.clearLanes && <Badge variant="secondary">CLEAR</Badge>}
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Live Wait Times CTA */}
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="py-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h2 className="text-xl font-semibold mb-1">Check Live TSA Wait Times</h2>
                      <p className="text-muted-foreground">See current security line wait at {airport.code}</p>
                    </div>
                    <Button asChild size="lg">
                      <Link to={`/airports/${code?.toLowerCase()}`}>
                        <Clock className="mr-2 h-4 w-4" />
                        See Live Wait Times
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Wait Times by Hour */}
              <section>
                <h2 className="text-2xl font-semibold mb-4">TSA Wait Times by Hour at {airport.code}</h2>
                <p className="text-muted-foreground mb-4">
                  Typical wait times throughout the day at {airport.name}. Actual times vary based on day and season.
                </p>
                <Card>
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {waitsByTime.map((slot, i) => (
                        <div key={i} className="flex items-center justify-between p-4">
                          <span className="font-medium">{slot.time}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-muted-foreground">{slot.wait}</span>
                            <Badge className={getLevelColor(slot.level)}>
                              {slot.level === "low" ? "Short" : slot.level === "medium" ? "Moderate" : "Long"}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </section>

              {/* Terminals */}
              <section>
                <h2 className="text-2xl font-semibold mb-4">{airport.code} Terminals</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {details.terminalNames.map((terminal, i) => (
                    <Card key={i}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Plane className="h-4 w-4 text-primary" />
                          {terminal}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          {i === 0 && details.hubAirlines.length > 0 
                            ? `Hub for ${details.hubAirlines[0]}` 
                            : "Multiple airlines"}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>

              {/* Security Tips */}
              <section>
                <h2 className="text-2xl font-semibold mb-4">Security Tips for {airport.code}</h2>
                <Card>
                  <CardContent className="py-4">
                    <ul className="space-y-3">
                      {details.tipsSecurity.map((tip, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </section>

              {/* FAQs */}
              <section>
                <h2 className="text-2xl font-semibold mb-4">
                  Frequently Asked Questions About {airport.code}
                </h2>
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, i) => (
                    <AccordionItem key={i} value={`faq-${i}`}>
                      <AccordionTrigger className="text-left">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            </div>

            {/* Sidebar */}
            <aside className="space-y-6">
              {/* Parking Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Car className="h-5 w-5" />
                    Parking at {airport.code}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span>Economy</span>
                    <span className="font-medium">{details.parking.economy}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Garage</span>
                    <span className="font-medium">{details.parking.garage}</span>
                  </div>
                  {details.parking.valet && (
                    <div className="flex justify-between">
                      <span>Valet</span>
                      <span className="font-medium">{details.parking.valet}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Transit Options */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Train className="h-5 w-5" />
                    Getting to {airport.code}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {details.transitOptions.map((option, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <ChevronRight className="h-3 w-3 text-muted-foreground" />
                        {option}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Peak Hours Warning */}
              <Card className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                    <AlertCircle className="h-5 w-5" />
                    Peak Hours
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-amber-700 dark:text-amber-300 space-y-2 text-sm">
                  <p><strong>Morning:</strong> {details.peakHours.morning}</p>
                  <p><strong>Evening:</strong> {details.peakHours.evening}</p>
                  <p><strong>Busiest Days:</strong> {details.peakHours.busiestDays.join(", ")}</p>
                </CardContent>
              </Card>

              {/* Hub Airlines */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Major Airlines
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {details.hubAirlines.map((airline, i) => (
                      <Badge key={i} variant="outline">{airline}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </aside>
          </div>
        </main>

        {/* Footer CTA */}
        <section className="bg-muted/50 py-12 mt-8">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl font-semibold mb-2">Flying from {airport.code} today?</h2>
            <p className="text-muted-foreground mb-6">Check real-time TSA wait times before you leave</p>
            <Button asChild size="lg">
              <Link to={`/airports/${code?.toLowerCase()}`}>
                Check {airport.code} Wait Times Now
              </Link>
            </Button>
          </div>
        </section>
      </div>
    </>
  );
}
